import { useEffect, useMemo, useRef, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Cascader, message, Card, Tag, Switch, Space, Collapse, Tooltip } from 'antd';
import type { DefaultOptionType } from 'antd/es/cascader';
import { PlusOutlined, EditOutlined, CopyOutlined, KeyOutlined, FileTextOutlined, SearchOutlined, LockOutlined, FolderOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import { useConfigStore } from '@/store/configStore';
import apiClient from '@/api/client';
import type { UserDetail, UserUploadMode } from '@/types';
import OperationLogModal from '@/components/OperationLogModal';
import {
  buildUserProjectCascaderOptions,
  cascaderValueToProjectIds,
  deriveSupplierNames,
  projectIdsToCascaderValue,
} from '@/utils/userProjectCascader';

const UNGROUPED_KEY = 'ungrouped';

const userProjectCascaderFilter = (input: string, path: DefaultOptionType[]) =>
  path.some(opt => String(opt.label).toLowerCase().includes(input.toLowerCase()));

const UserManagement = () => {
  const { projects, mountains, fetchProjects, fetchMountains } = useConfigStore();
  const [users, setUsers] = useState<UserDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState<UserDetail | null>(null);
  const [passwordForm] = Form.useForm();
  const [editingUser, setEditingUser] = useState<UserDetail | null>(null);
  const [logUser, setLogUser] = useState<UserDetail | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [resetResult, setResetResult] = useState<{ username: string; new_password: string } | null>(null);
  const resetLoginInfoRef = useRef<HTMLTextAreaElement>(null);
  const [createResult, setCreateResult] = useState<{ username: string; password: string } | null>(null);
  const createLoginInfoRef = useRef<HTMLTextAreaElement>(null);

  // 用户的底软供应商不再手填，由他勾选的可访问项目推导
  const watchedProjectIds = Form.useWatch<number[]>('project_ids', form);
  const derivedSuppliers = useMemo(
    () => deriveSupplierNames(watchedProjectIds ?? [], projects),
    [watchedProjectIds, projects],
  );

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // The backend paginates at 100 rows, so walk every page and accumulate.
      const all: UserDetail[] = [];
      let page = 1;
      for (;;) {
        const response = await apiClient.get<any>('/auth/users/', {
          params: { user_type: 'external', detail: 'true', page, page_size: 100 },
        });
        const data = response.data;
        if (Array.isArray(data)) {
          all.push(...data);
          break;
        }
        all.push(...(data.results || []));
        if (!data.next) break;
        page += 1;
      }
      setUsers(all);
    } catch {
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchProjects();
    fetchMountains();
  }, [fetchProjects, fetchMountains]);

  const userProjectCascaderOptions = useMemo(
    () => buildUserProjectCascaderOptions(projects, mountains),
    [projects, mountains],
  );

  const handleUserSubmit = async (values: Record<string, unknown>) => {
    const { project_ids, ...userValues } = values;
    try {
      if (editingUser) {
        await apiClient.put(`/auth/users/${editingUser.id}/`, userValues);
        await apiClient.put(`/auth/users/${editingUser.id}/permissions/`, { project_ids });
        message.success('更新成功');
      } else {
        await apiClient.post('/auth/users/', values);
        const username = values.username as string;
        const password = values.password as string;
        setCreateResult({ username, password });
      }
      setIsUserModalOpen(false);
      setEditingUser(null);
      form.resetFields();
      fetchUsers();
    } catch {
      message.error('操作失败');
    }
  };

  const handleChangePassword = async (values: { password: string }) => {
    if (!passwordUser) return;
    try {
      await apiClient.put(`/auth/users/${passwordUser.id}/`, { password: values.password });
      message.success('密码修改成功');
      setIsPasswordModalOpen(false);
      setPasswordUser(null);
      passwordForm.resetFields();
      fetchUsers();
    } catch {
      message.error('密码修改失败');
    }
  };

  const handleResetPassword = (user: UserDetail) => {
    Modal.confirm({
      title: '重置密码',
      content: `确定要重置用户 "${user.display_name || user.username}" 的密码吗？`,
      onOk: async () => {
        try {
          const response = await apiClient.post<{ username: string; new_password: string; message: string }>(
            `/auth/users/${user.id}/reset_password/`
          );

          const { username, new_password } = response.data;
          fetchUsers();
          setResetResult({ username, new_password });
        } catch (error) {
          message.error('密码重置失败');
        }
      },
    });
  };

  const filteredUsers = users.filter((u) =>
    u.username.toLowerCase().includes(searchText.toLowerCase())
  );

  const projectMountainMap = useMemo(() => {
    const map = new Map<number, { id: number; name: string }>();
    projects.forEach((p) => {
      if (p.mountain) map.set(p.id, p.mountain);
    });
    return map;
  }, [projects]);

  const groupedUsers = useMemo(() => {
    const usersByMountainId = new Map<number, UserDetail[]>();
    const mountainInfoById = new Map<number, { id: number; name: string }>();
    const ungrouped: UserDetail[] = [];

    filteredUsers.forEach((user) => {
      const mountainIds = new Set<number>();
      (user.permitted_projects || []).forEach((p) => {
        const mountain = projectMountainMap.get(p.id);
        if (mountain) {
          mountainIds.add(mountain.id);
          mountainInfoById.set(mountain.id, mountain);
        }
      });

      if (mountainIds.size === 0) {
        ungrouped.push(user);
      } else {
        mountainIds.forEach((id) => {
          const list = usersByMountainId.get(id) || [];
          list.push(user);
          usersByMountainId.set(id, list);
        });
      }
    });

    // Build groups from BVP-sourced project data; use BSP mountains only for sort order (match by name, not ID)
    const bspOrderMap = new Map(mountains.map((m) => [m.name.toLowerCase(), m.sort_order ?? 999]));
    const groups = Array.from(usersByMountainId.entries())
      .sort(([idA], [idB]) => {
        const nameA = mountainInfoById.get(idA)?.name ?? '';
        const nameB = mountainInfoById.get(idB)?.name ?? '';
        const orderA = bspOrderMap.get(nameA.toLowerCase()) ?? 999;
        const orderB = bspOrderMap.get(nameB.toLowerCase()) ?? 999;
        if (orderA !== orderB) return orderA - orderB;
        return nameA.localeCompare(nameB);
      })
      .map(([id, users]) => ({
        key: `mountain-${id}`,
        name: mountainInfoById.get(id)?.name ?? String(id),
        users,
      }));

    if (ungrouped.length > 0) {
      groups.push({ key: UNGROUPED_KEY, name: '未分组', users: ungrouped });
    }

    return groups;
  }, [filteredUsers, mountains, projectMountainMap]);

  const columns: ColumnsType<UserDetail> = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      sorter: (a, b) => a.username.localeCompare(b.username),
    },
    { title: '显示名称', dataIndex: 'display_name', key: 'display_name' },
    { title: '邮箱', dataIndex: 'email', key: 'email' },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>{active ? '启用' : '禁用'}</Tag>
      ),
    },
    {
      title: '可访问项目',
      key: 'projects',
      render: (_, record) => (
        <span>{record.permitted_projects?.length || 0} 个项目</span>
      ),
    },
    {
      title: '上传模式',
      key: 'upload_mode',
      width: 100,
      render: (_, record) => {
        const mode = record.upload_mode ?? 'auto';
        return <Tag color={mode === 'proxy' ? 'orange' : 'default'}>{mode}</Tag>;
      },
    },
    {
      title: '底软供应商',
      key: 'software_supplier',
      render: (_, record) => {
        const names = deriveSupplierNames(
          record.permitted_projects?.map((p) => p.id) ?? [],
          projects,
        );
        return names.length ? names.join('、') : '-';
      },
    },
    {
      title: '密码修改时间',
      key: 'password_changed_at',
      render: (_, record) =>
        record.password_changed_at
          ? new Date(record.password_changed_at).toLocaleString('zh-CN')
          : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setEditingUser(record);
                form.setFieldsValue({
                  ...record,
                  project_ids: record.permitted_projects?.map((p) => p.id) || [],
                });
                setIsUserModalOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="修改密码">
            <Button
              type="text"
              size="small"
              icon={<LockOutlined />}
              onClick={() => {
                setPasswordUser(record);
                passwordForm.resetFields();
                setIsPasswordModalOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="重置密码">
            <Button
              type="text"
              size="small"
              icon={<KeyOutlined />}
              onClick={() => handleResetPassword(record)}
            />
          </Tooltip>
          <Tooltip title="日志">
            <Button
              type="text"
              size="small"
              icon={<FileTextOutlined />}
              onClick={() => {
                setLogUser(record);
                setIsLogModalOpen(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Card title="外部用户管理">
      <div className="mb-4" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingUser(null);
            form.resetFields();
            setIsUserModalOpen(true);
          }}
        >
          新增用户
        </Button>
        <Input
          placeholder="搜索用户名"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ width: 220 }}
        />
      </div>

      <Collapse
        items={groupedUsers.map((group) => ({
          key: group.key,
          label: (
            <span>
              <FolderOutlined className="mr-2 text-blue-500" />
              {group.name}
              <Tag className="ml-2" color="blue">{group.users.length} 个用户</Tag>
            </span>
          ),
          children: (
            <Table columns={columns} dataSource={group.users} rowKey="id" loading={loading} pagination={false} />
          ),
        }))}
      />

      {/* User Modal */}
      <Modal
        title={editingUser ? '编辑用户' : '新增用户'}
        open={isUserModalOpen}
        onCancel={() => setIsUserModalOpen(false)}
        onOk={() => form.submit()}
      >
        {/* autoComplete 必须显式关掉：Chrome 会把「用户名 + 密码」当成登录表单，
            打开弹窗时自动填入已保存的账号密码 */}
        <Form form={form} layout="vertical" onFinish={handleUserSubmit} autoComplete="off">
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true }]}
          >
            <Input disabled={!!editingUser} autoComplete="off" />
          </Form.Item>
          {!editingUser && (
            <Form.Item
              name="password"
              label="密码"
              rules={[{ required: true, min: 8 }]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
          )}
          <Form.Item name="display_name" label="显示名称" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="邮箱">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item name="upload_mode" label="上传模式" initialValue="auto">
            <Select<UserUploadMode>
              options={[
                { label: 'Auto（自动）', value: 'auto' },
                { label: 'Proxy（代理）', value: 'proxy' },
              ]}
            />
          </Form.Item>
          <Form.Item label="底软供应商" tooltip="由「可访问项目」自动推导，不可直接编辑">
            <Input
              readOnly
              value={derivedSuppliers.join('、')}
              placeholder="-"
              style={{ background: '#f5f5f5', cursor: 'default' }}
            />
          </Form.Item>
          <Form.Item
            name="project_ids"
            label="可访问项目"
            initialValue={[]}
            getValueFromEvent={cascaderValueToProjectIds}
            getValueProps={(ids: number[] | undefined) => ({
              value: projectIdsToCascaderValue(ids || [], projects),
            })}
          >
            <Cascader
              multiple
              // 必须 SHOW_CHILD：默认的 SHOW_PARENT 会把勾满的子节点折叠成父路径，
              // 父路径末段不是项目 id，会被 cascaderValueToProjectIds 丢掉，勾选随即回弹
              showCheckedStrategy={Cascader.SHOW_CHILD}
              options={userProjectCascaderOptions}
              showSearch={{ filter: userProjectCascaderFilter }}
              placeholder="选择项目"
              expandTrigger="click"
              displayRender={labels => labels[labels.length - 1]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        title={`修改密码 - ${passwordUser?.display_name || passwordUser?.username}`}
        open={isPasswordModalOpen}
        onCancel={() => {
          setIsPasswordModalOpen(false);
          setPasswordUser(null);
          passwordForm.resetFields();
        }}
        onOk={() => passwordForm.submit()}
        destroyOnHidden
      >
        <Form form={passwordForm} layout="vertical" onFinish={handleChangePassword}>
          <Form.Item
            name="password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              {
                validator(_, value) {
                  if (!value) return Promise.resolve();
                  if (value.length <= 10)
                    return Promise.reject(new Error('密码长度必须大于10位'));
                  if (!/[0-9]/.test(value))
                    return Promise.reject(new Error('密码必须包含数字'));
                  if (!/[a-zA-Z]/.test(value))
                    return Promise.reject(new Error('密码必须包含字母'));
                  if (!/[^a-zA-Z0-9]/.test(value))
                    return Promise.reject(new Error('密码必须包含特殊字符'));
                  if (passwordUser && value === passwordUser.username)
                    return Promise.reject(new Error('密码不能与账号名相同'));
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="大于10位，含数字、字母和特殊字符" />
          </Form.Item>
          <Form.Item
            name="confirm"
            label="确认密码"
            dependencies={['password']}
            rules={[
              { required: true, message: '请确认密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) return Promise.resolve();
                  return Promise.reject(new Error('两次密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Operation Log Modal */}
      {logUser && (
        <OperationLogModal
          open={isLogModalOpen}
          onCancel={() => {
            setIsLogModalOpen(false);
            setLogUser(null);
          }}
          targetType="user"
          targetId={logUser.id}
          title={`${logUser.display_name || logUser.username} - 操作日志`}
        />
      )}

      {/* Create User Result Modal */}
      <Modal
        title="用户创建成功"
        open={!!createResult}
        onCancel={() => setCreateResult(null)}
        footer={<Button onClick={() => setCreateResult(null)}>关闭</Button>}
        width={420}
      >
        {createResult && (
          <div>
            <div style={{ padding: '12px', background: '#f0f0f0', borderRadius: '4px', marginBottom: '12px' }}>
              <div><strong>用户名：</strong>{createResult.username}</div>
              <div><strong>初始密码：</strong><span style={{ fontFamily: 'monospace' }}>{createResult.password}</span></div>
            </div>
            <textarea
              ref={createLoginInfoRef}
              readOnly
              value={`用户名：${createResult.username}\n密码：${createResult.password}`}
              style={{ position: 'absolute', left: '-9999px', top: 0 }}
            />
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={() => {
                const el = createLoginInfoRef.current;
                if (!el) return;
                el.style.left = '0';
                el.focus();
                el.select();
                try {
                  document.execCommand('copy');
                  message.success('已复制到剪贴板');
                } catch {
                  message.error('复制失败，请手动复制');
                }
                el.style.left = '-9999px';
              }}
              block
            >
              复制登录信息
            </Button>
            <div style={{ marginTop: '8px', color: '#999', fontSize: '12px' }}>
              请将登录信息告知用户，建议用户首次登录后修改密码
            </div>
          </div>
        )}
      </Modal>

      {/* Reset Password Result Modal */}
      <Modal
        title="密码重置成功"
        open={!!resetResult}
        onCancel={() => setResetResult(null)}
        footer={<Button onClick={() => setResetResult(null)}>关闭</Button>}
        width={420}
      >
        {resetResult && (
          <div>
            <div style={{ padding: '12px', background: '#f0f0f0', borderRadius: '4px', marginBottom: '12px' }}>
              <div><strong>用户名：</strong>{resetResult.username}</div>
              <div><strong>新密码：</strong><span style={{ fontFamily: 'monospace', fontSize: '16px' }}>{resetResult.new_password}</span></div>
            </div>
            <textarea
              ref={resetLoginInfoRef}
              readOnly
              value={`用户名：${resetResult.username}\n新密码：${resetResult.new_password}`}
              style={{ position: 'absolute', left: '-9999px', top: 0 }}
            />
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={() => {
                const el = resetLoginInfoRef.current;
                if (!el) return;
                el.style.left = '0';
                el.focus();
                el.select();
                try {
                  document.execCommand('copy');
                  message.success('已复制到剪贴板');
                } catch {
                  message.error('复制失败，请手动复制');
                }
                el.style.left = '-9999px';
              }}
              block
            >
              复制登录信息
            </Button>
            <div style={{ marginTop: '12px', color: '#999', fontSize: '12px' }}>
              请将新密码告知用户，建议用户登录后修改密码
            </div>
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default UserManagement;
